import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Building2 } from 'lucide-react';
import { Cartao } from '../../componentes/base';
import { Estado, Vazio } from '../../componentes/estados';
import { get } from '../../nucleo/api';
import { data, numero } from '../../nucleo/formato';
import { Barra, BotaoExportar, Rolagem, TD, TH, usePeriodo, Valor } from './comum';
import { SeletorIntercompany, useIntercompany } from './Dre';
import type { IndicadorPainel, PainelGrupo as PainelGrupoApi, ValorIndicadorPainel } from './tipos';

/** Linhas de destaque do painel (as mesmas classes da cascata da DRE). */
const DESTAQUE: Partial<Record<IndicadorPainel['chave'], string>> = { resultado: 'font-semibold bg-sup-2' };

/**
 * Linhas da tabela: uma por indicador visível e moeda (o ocultado, sem a leitura do módulo dono, não aparece) e se a coluna
 * Eliminações aparece (operações do grupo eliminadas e algum valor diferente de zero).
 */
export function linhasDoPainel(d: PainelGrupoApi) {
  const visiveis = d.indicadores.filter((i) => !i.ocultado);
  const comEliminacoes = d.intercompany === 'eliminados' && visiveis.some((i) => i.valores.some((v) => v.eliminacoes !== 0));
  const linhas = visiveis.flatMap((i) => i.valores.map((v) => ({ i, v, varias: i.valores.length > 1 })));
  return { linhas, comEliminacoes, comOcultos: d.indicadores.some((i) => i.ocultado) };
}

/**
 * Lote 19 (item 3 do lote de 19/10): painel do grupo de empresas. Uma coluna por empresa ativa e o total, com os
 * indicadores do período (realizados no caixa) e a posição de hoje. A aba só aparece para quem vê todas as empresas com o
 * consolidado; o indicador sem a leitura do módulo dono não aparece (o servidor o manda ocultado).
 */
export function PainelGrupo() {
  const { t } = useTranslation('relatorios');
  const { periodo, seletor } = usePeriodo();
  const [inter, setInter, interVigente] = useIntercompany();
  const params = { de: periodo.de, ate: periodo.ate, intercompany: inter };
  const q = useQuery({ queryKey: ['/relatorios/painel-grupo', params], queryFn: () => get<PainelGrupoApi>('/relatorios/painel-grupo', params), placeholderData: keepPreviousData });

  const celula = (i: IndicadorPainel, v: number, moeda: string | null) =>
    i.formato === 'dinheiro' ? <Valor centavos={v} moeda={moeda ?? ''} /> : <span className="numero whitespace-nowrap">{numero(v)}</span>;

  return (
    <div className="flex min-w-0 flex-col gap-4">
      <Barra>
        {seletor}
        <SeletorIntercompany valor={interVigente} aoMudar={setInter} />
        <BotaoExportar relatorio="painel_grupo" parametros={params} />
      </Barra>
      <Estado consulta={q}>
        {(d) => {
          const { linhas, comEliminacoes, comOcultos } = linhasDoPainel(d);
          const rotulo = (i: IndicadorPainel, v: ValorIndicadorPainel, varias: boolean) => `${t(`grupo.indicadores.${i.chave}`)}${varias && v.moeda ? ` (${v.moeda})` : ''}`;
          return (
            <>
              <p className="text-sm text-tinta-3">{t('grupo.referencia', { de: data(d.de), ate: data(d.realizado_ate), saldo: data(d.saldo_em) })}</p>
              {d.empresas.length === 0 ? (
                <Cartao>
                  <Vazio icone={<Building2 className="size-5" aria-hidden />} titulo={t('grupo.vazioTitulo')} texto={t('grupo.vazioTexto')} />
                </Cartao>
              ) : (
                <Cartao semPadding>
                  <Rolagem rotulo={t('grupo.tabela')}>
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-borda text-left text-tinta-3">
                          <th scope="col" className={TH}>{t('grupo.indicador')}</th>
                          {d.empresas.map((e) => (
                            <th key={e.id} scope="col" className={`${TH} text-right`}>{e.nome}</th>
                          ))}
                          {comEliminacoes && <th scope="col" className={`${TH} text-right`}>{t('dre.eliminacoes')}</th>}
                          <th scope="col" className={`${TH} text-right`}>{t('comum.total')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {linhas.map(({ i, v, varias }) => (
                          <tr key={`${i.chave}:${v.moeda ?? ''}`} className={`border-b border-borda last:border-0 ${DESTAQUE[i.chave] ?? ''}`}>
                            <th scope="row" className={`${TD} text-left break-words ${DESTAQUE[i.chave] ? '' : 'font-normal'}`}>
                              {rotulo(i, v, varias)}
                            </th>
                            {d.empresas.map((e) => (
                              <td key={e.id} className={`${TD} text-right`}>
                                {celula(i, v.por_empresa[e.id] ?? 0, v.moeda)}
                              </td>
                            ))}
                            {comEliminacoes && <td className={`${TD} text-right text-tinta-2`}>{v.eliminacoes !== 0 ? celula(i, v.eliminacoes, v.moeda) : ''}</td>}
                            <td className={`${TD} text-right font-semibold`}>{celula(i, v.total, v.moeda)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </Rolagem>
                  <p className="border-t border-borda px-4 py-3 text-xs text-tinta-3">
                    {d.intercompany === 'eliminados' ? t('grupo.ajudaEliminados') : t('grupo.ajudaSeparados')}
                    {comOcultos ? ` ${t('grupo.ocultos')}` : ''}
                  </p>
                </Cartao>
              )}
            </>
          );
        }}
      </Estado>
    </div>
  );
}
